import Vue from 'vue'
import { Mixin } from 'vue-mixin-decorator'

import { log } from '@/log'

interface LockBackgroundOwner {
  getName: () => string
}

/**
 * body/htmlのスクロールロックはページ全体に対する副作用なので、コンポーネント単位 (mixin単位) で管理すると
 * 複数のV2利用コンポーネントが同時にマウントされた時に、先に破棄された側が全体のロックを解除してしまう。
 * managerで所有者を一元管理し、最初のownerでlock、最後のownerでunlockする。
 */
const lockBackgroundV2Manager = new class {
  private originalOverflowOfBody = ''
  private originalOverflowOfHtml = ''
  private owners = new Set<LockBackgroundOwner>()

  lock(owner: LockBackgroundOwner): void {
    log.debug(`${owner.getName()} requested LockBackgroundV2 lock.`)
    if (this.owners.has(owner)) {
      log.warn(`${owner.getName()} requested LockBackgroundV2 lock more than once before unlock.`)

      return
    }

    const isFirstOwner = this.owners.size === 0
    this.owners.add(owner)

    if (isFirstOwner) {
      this.doLock()
    }
  }

  unlock(owner: LockBackgroundOwner): void {
    log.debug(`${owner.getName()} requested LockBackgroundV2 unlock.`)
    if (!this.owners.has(owner)) {
      log.warn(`${owner.getName()} requested LockBackgroundV2 unlock before lock.`)

      return
    }

    this.owners.delete(owner)

    if (this.owners.size === 0) {
      this.doUnlock()
    }
  }

  private doLock(): void {
    /**
     * html, bodyのクラスを変更するとDOMの再計算が行われてINPが悪くなるため、スタイルを直接変更する
     * @see https://github.com/Everforth/growthpalette-front/issues/7839
     *
     * V2のロック状態はこのmanagerだけが保持する。
     * 複数の利用コンポーネントが同時にマウントされても、overflowの保存と復元は一度だけ行う。
     */
    /**
     * デフォルト値としては、スタイル宣言のリセットを表す空文字列を設定する
     * https://developer.mozilla.org/ja/docs/Web/API/HTMLElement/style
     *
     * NOTE:
     * originalOverflowOfBodyとして hidden は採用しないが、これは LockBackground (V1と呼称) との併存期間に限定する処置。
     * V1とV2が併用された際、overflowをすでにV1によってhiddenに変更されているとV2はそれを記録し、
     * V2の解除時にhiddenに「戻す」ため、全画面のスクロールロックが継続するというクリティカルな挙動になり得る。
     * これを回避するためにhiddenは回避しているが、この実装はV2だけになれば不要となるので削除する。
     */
    const bodyOverflow = document.body.style.overflow
    this.originalOverflowOfBody = bodyOverflow === 'hidden' ? '' : (bodyOverflow || '')
    document.body.style.overflow = 'hidden'

    const htmlTag = document.getElementsByTagName('html')[0]
    if (htmlTag) {
      const htmlOverflow = htmlTag.style.overflow
      this.originalOverflowOfHtml = htmlOverflow === 'hidden' ? '' : (htmlOverflow || '')
      htmlTag.style.overflow = 'hidden'
    }

    log.debug({ service: 'LockBackgroundV2/doLock', owners: this.owners })
  }

  private doUnlock(): void {
    document.body.style.overflow = this.originalOverflowOfBody

    const htmlTag = document.getElementsByTagName('html')[0]
    if (htmlTag) {
      htmlTag.style.overflow = this.originalOverflowOfHtml
    }

    log.debug({ service: 'LockBackgroundV2/doUnlock', owners: this.owners })
  }
}()

/**
 * 背景スクロールをロックする mixin。マウント中、html/body の overflow を hidden にしてページ全体のスクロールを止める。
 * LockBackground (V1) と同時に利用すると、V2による背景ロックがV1によって解除されることもあるが移行期ということもあり許容としている。
 */
@Mixin
export default class LockBackgroundV2 extends Vue {
  private readonly lockBackgroundOwner: LockBackgroundOwner = {
    getName: () => this.$options.name || 'component',
  }

  mounted() {
    lockBackgroundV2Manager.lock(this.lockBackgroundOwner)
  }

  beforeDestroy() {
    lockBackgroundV2Manager.unlock(this.lockBackgroundOwner)
  }
}
